import { type SxProps, type Theme, Typography } from "@mui/material";
import type { ElementType, FC, ReactNode } from "react";
import { FONT_SIZE, INK, LETTER_SPACING } from "../../../theme/tokens";

type Props = {
	/** Already translated. A few words naming what follows ("How many", "Called in at"). */
	children: ReactNode;
	/**
	 * The element it renders as, for the document outline: `h2`/`h3` when it heads a section, `dt` for a term in a
	 * description list, `span` inside phrasing content.
	 * Absent, a plain `p` — a label over a control, which is what most of these are.
	 */
	component?: ElementType;
	/**
	 * Placement and surface only — margins, padding, an alignment, a band's background (`SheetStepSectionLabel` sets
	 * the label in one). Never its type — size, weight, tracking, case, ink: those are the point of the component.
	 */
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * The house uppercase eyebrow: a small, tracked, muted label over a control, a figure or a run of rows — "How many",
 * "Reason", "Called in at", a fact's term on a card.
 *
 * One component because the same five properties (`LABEL_XS`, bold, `LETTER_SPACING.LABEL`, uppercase, `INK.MUTED`)
 * were being restated beside every figure and field on the phone's sheets, and a copy that drifts by one property
 * makes two labels on one card read as two kinds of thing. `SheetStepSectionLabel` is this label set in a band.
 */
export const EyebrowLabel: FC<Props> = ({ children, component, sx, dataTestId }) => (
	<Typography
		// DEFAULT: Typography's own element for its body variant — the plain-label case, not a missing value.
		component={component ?? "p"}
		data-testid={dataTestId}
		sx={[
			{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: LETTER_SPACING.LABEL, textTransform: "uppercase", color: INK.MUTED },
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	>
		{children}
	</Typography>
);
